<script lang="ts">
  import * as Drawer from "$lib/components/ui/drawer";
  import { hubStore } from "$src/application/realtime/hubStore";
  import { UserAction } from "$src/domain/realtime/UserAction";
  import { connectedUsers } from "$src/application/realtime/connectedUserStore";
  import {
    autoTakeOverTimerValue,
    clearTakeOverRequests,
    isTakeOverQueryInProgress,
    takeOverDialogVisible,
  } from "$src/application/realtime/draftStore";
  import TimeSinceHour from "$src/presentation/core/time-since-hour.svelte";
  import { get } from "svelte/store";
  import { useTakeOverDraft } from "../../hooks/useTakeOverDraft";
  import { Button } from "$lib/components/ui/button";
  import type { DraftPermission } from "$src/domain/projects/drafts/DraftPermission";
  import { t } from "svelte-i18n";

  export let projectId: number;
  export let groupStoreId: number;
  export let draftPermission: DraftPermission;

  const takeOverCommand = useTakeOverDraft();

  function isEditingInProgress(): boolean {
    return (
      get(connectedUsers).find(
        (user) =>
          user.action == UserAction.EditingProject &&
          user.location == projectId.toString()
      ) != undefined
    );
  }

  function queryTakingOver() {
    get(hubStore)?.queryTakingOverProject(groupStoreId, projectId);
  }

  function takeOver() {
    clearTakeOverRequests();
    $takeOverCommand.mutate({ groupStoreId, projectId });
  }

  function onCloseDraftInProgress() {
    get(hubStore)?.cancelTakingOverProject(groupStoreId, projectId);
  }
</script>

<Drawer.Root
  bind:open={$takeOverDialogVisible}
  onClose={onCloseDraftInProgress}
>
  <Drawer.Content>
    <div class="mx-auto w-full max-w-sm">
      <Drawer.Header>
        <Drawer.Title>
          {#if isEditingInProgress()}
            {$t("page.projects.draft.editingInProgressTitle")}
            {#if $isTakeOverQueryInProgress}
              <div class="text-sm">
                {$t("page.projects.draft.takeOverSent", {
                  values: { autoTakeOverTimerValue: $autoTakeOverTimerValue },
                })}
              </div>
            {/if}
          {:else}
            {$t("page.projects.draft.existingDraftTitle")}
          {/if}
        </Drawer.Title>
        <Drawer.Description>
          <div class="flex flex-col gap-2">
            {#if isEditingInProgress()}
              <div>
                <span class="inline-block w-2 h-2 bg-green-500 rounded-full" />
                <div class="font-medium gap-2 items-center inline">
                  {draftPermission.projectDraft.editor.firstname}
                  {draftPermission.projectDraft.editor.lastname}
                </div>
                {$t("page.projects.draft.userEditingProjectLongText")}
              </div>
              <div class="grid grid-cols-2 gap-2 text-xs">
                <div class="text-right">
                  {$t("global.messages.dateStart")}
                </div>
                <div>
                  <TimeSinceHour
                    dateString={draftPermission.projectDraft.creationDate}
                  />
                </div>
                <div class="text-right">
                  {$t("global.messages.lastUpdate")}
                </div>
                <div>
                  {#if draftPermission.projectDraft.modificationDate}
                    <TimeSinceHour
                      dateString={draftPermission.projectDraft.modificationDate}
                    />
                  {:else}
                    /
                  {/if}
                </div>
              </div>
            {:else}
              <div>
                {$t("page.projects.draft.draftExistingLongText")}
              </div>
              <div class="grid grid-cols-2 gap-2 text-xs">
                <div class="text-right">
                  {$t("global.messages.user")}
                </div>
                <div class="font-semibold">
                  {draftPermission.projectDraft.editor.firstname}
                  {draftPermission.projectDraft.editor.lastname}
                </div>
                <div class="text-right">
                  {$t("global.messages.createdAt")}
                </div>
                <div>
                  <TimeSinceHour
                    dateString={draftPermission.projectDraft.creationDate}
                  />
                </div>
                <div class="text-right">
                  {$t("global.messages.lastUpdate")}
                </div>
                <div>
                  {#if draftPermission.projectDraft.modificationDate}
                    <TimeSinceHour
                      dateString={draftPermission.projectDraft.modificationDate}
                    />
                  {:else}
                    /
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        </Drawer.Description>
      </Drawer.Header>
      <Drawer.Footer class="flex justify-center">
        <div class="flex flex-row justify-center max-w-md">
          {#if isEditingInProgress()}
            <Button
              class="flex-1"
              on:click={queryTakingOver}
              disabled={$isTakeOverQueryInProgress}
            >
              {#if $isTakeOverQueryInProgress}
                {$t("page.projects.draft.takeOverSent", {
                  values: { autoTakeOverTimerValue: $autoTakeOverTimerValue },
                })}
              {:else}
                {$t("page.projects.draft.takeOver", {
                  values: { autoTakeOverTimerValue: $autoTakeOverTimerValue },
                })}
              {/if}
            </Button>
          {:else}
            <Button class="flex-1" on:click={takeOver}>
              {$t("page.projects.draft.edit")}
            </Button>
          {/if}
          <Drawer.Close class="flex-1">
            {$t("global.messages.cancel")}
          </Drawer.Close>
        </div>
      </Drawer.Footer>
    </div>
  </Drawer.Content>
</Drawer.Root>
